import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Button, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { FnPageLogoDown } from '../../Public/Home/Index';
import overviewUrl from './overview.mdx';
import './Documentation.css';

const DOWNLOAD_FILENAME = 'overview.mdx';

/** Remove o frontmatter YAML (--- ... ---) do início do MDX. */
function stripFrontmatter(raw) {
  if (!raw.startsWith('---')) {
    return { meta: {}, body: raw };
  }
  const end = raw.indexOf('\n---', 3);
  if (end === -1) {
    return { meta: {}, body: raw };
  }
  const front = raw.slice(3, end).trim();
  const body = raw.slice(end + 4).replace(/^\n+/, '');
  const meta = {};
  for (const line of front.split('\n')) {
    const i = line.indexOf(':');
    if (i === -1) continue;
    const key = line.slice(0, i).trim();
    const value = line.slice(i + 1).trim();
    meta[key] = value;
  }
  return { meta, body };
}

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Documentation() {
  const [raw, setRaw] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(overviewUrl);
        if (!res.ok) {
          throw new Error(`Não foi possível carregar ${overviewUrl} (${res.status})`);
        }
        const text = await res.text();
        if (!cancelled) {
          setRaw(text);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Erro ao carregar a documentação');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { meta, body } = useMemo(() => stripFrontmatter(raw || ''), [raw]);

  const handleDownload = useCallback(() => {
    if (!raw) return;
    downloadTextFile(DOWNLOAD_FILENAME, raw);
  }, [raw]);

  return (
    <FnPageLogoDown>
      <Box className="documentation-page">
        <Box className="documentation-toolbar">
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={!raw}
            sx={{
              color: '#ffc356',
              borderColor: 'rgba(255, 195, 86, 0.55)',
              '&:hover': {
                borderColor: '#ffc356',
                backgroundColor: 'rgba(255, 195, 86, 0.08)',
              },
            }}
          >
            Baixar overview.mdx
          </Button>
        </Box>

        {loading && (
          <Typography sx={{ color: '#cccccc', textAlign: 'center', mt: 4 }}>
            Carregando documentação…
          </Typography>
        )}

        {error && (
          <Typography sx={{ color: 'error.main', textAlign: 'center', mt: 4, px: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <Box className="documentation-card">
            {(meta.title || meta.description) && (
              <Box className="documentation-meta" sx={{ mb: 3 }}>
                {meta.title && (
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
                    {meta.title}
                  </Typography>
                )}
                {meta.description && (
                  <Typography sx={{ color: '#cccccc', mt: 1 }}>
                    {meta.description}
                  </Typography>
                )}
              </Box>
            )}

            <Box className="documentation-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
            </Box>
          </Box>
        )}
      </Box>
    </FnPageLogoDown>
  );
}
